<template>
  <div class="w-full max-w-full min-w-0 mx-auto p-2 sm:p-4 bg-white dark:bg-gray-800 rounded-lg">
    <div v-if="overallStatus !== ''" class="alert mb-4 rounded-box px-4 py-3 shadow-sm" :class="statusAlertClass" :dir="isRtl === true ? 'rtl' : 'ltr'">
      <div class="flex w-full flex-col items-start justify-between sm:items-center" :class="isRtl === true ? 'sm:flex-row-reverse' : 'sm:flex-row'">
        <div class="flex items-center min-w-0" :class="isRtl === true ? 'text-left' : 'text-right'">
          <span v-if="isActiveStatus === true" class="loading loading-spinner loading-md flex-shrink-0" :class="isRtl === true ? 'mr-3' : 'ml-3'"></span>
          <Icon v-else-if="overallStatus === 'completed'" icon="tabler:circle-check-filled" class="h-7 w-7 flex-shrink-0" :class="isRtl === true ? 'mr-3' : 'ml-3'" />
          <Icon v-else-if="overallStatus === 'failed'" icon="tabler:alert-circle-filled" class="h-7 w-7 flex-shrink-0" :class="isRtl === true ? 'mr-3' : 'ml-3'" />
          <Icon v-else icon="tabler:alert-triangle-filled" class="h-7 w-7 flex-shrink-0" :class="isRtl === true ? 'mr-3' : 'ml-3'" />
          <div class="min-w-0">
            <div class="font-semibold">{{ statusTitle }}</div>
            <div class="text-sm opacity-80 break-words">{{ statusMessage }}</div>
          </div>
        </div>
        <div class="badge badge-lg mt-3 sm:mt-0 flex-shrink-0" dir="ltr">
          {{ completedCount }} / {{ discoveredCount }}
        </div>
      </div>
    </div>
    <div role="tablist" class="tabs tabs-lifted w-full min-w-0">
      <input type="radio" role="tab" class="tab" aria-label="Table" :checked="activeTab==='table'" @click="activeTab='table'" />
      <div role="tabpanel" class="tab-content bg-base-100 border-base-300 rounded-box p-4 w-full min-w-0" v-show="activeTab==='table'">
        <h3 class="text-lg font-semibold mb-4 break-all">
          {{ t('aiChats.scrapeReportTitle', 'Scrape Report for:') }} {{ props.source.url }}
        </h3>
        <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-3 grid-space-2">
          <input type="text" v-model="filterQuery" placeholder="Search..." class="input input-bordered w-full sm:w-1/2" />
          <select v-model="statusFilter" class="select select-bordered w-full sm:w-1/4">
            <option value="">{{ t('aiChats.scrapeReport.allStatuses','All Statuses') }}</option>
            <option v-for="status in uniqueStatuses" :key="status" :value="status">{{ status }}</option>
          </select>
        </div>
        <div class="w-full max-w-full overflow-auto max-h-[calc(100vh-22rem)]">
          <table class="w-full min-w-[720px] divide-y divide-gray-200 dark:divide-gray-600">
            <thead class="bg-gray-50 dark:bg-gray-700 sticky top-0 z-10">
              <tr>
                <th class="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{{ t('aiChats.scrapeReport.columnUrl','URL') }}</th>
                <th class="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{{ t('aiChats.scrapeReport.columnStatus','Status') }}</th>
                <th class="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{{ t('aiChats.scrapeReport.columnAttempts','Attempts') }}</th>
                <th class="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{{ t('aiChats.scrapeReport.columnError','Error') }}</th>
                <th class="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{{ t('aiChats.scrapeReport.columnProcessed','Processed At') }}</th>
              </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-200 dark:bg-gray-800 dark:divide-gray-600">
              <tr v-for="entry in filteredReport" :key="entry.url" class="hover:bg-gray-100 dark:hover:bg-gray-700">
                <td class="px-4 py-2 whitespace-normal break-all text-sm text-gray-900 dark:text-gray-100" :title="entry.url">{{ entry.url }}</td>
                <td class="px-4 py-2 whitespace-nowrap"><span :class="statusBadgeClass(entry.status)">{{ entry.status }}</span></td>
                <td class="px-4 py-2 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">{{ entry.attempts }}</td>
                <td class="px-4 py-2 whitespace-normal break-words text-sm text-red-600 dark:text-red-400" :title="entry.lastError">{{ entry.lastError || '-' }}</td>
                <td class="px-4 py-2 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">{{ entry.processedAt ? new Date(entry.processedAt).toLocaleString() : '-' }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <input type="radio" role="tab" class="tab" aria-label="Dashboard" :checked="activeTab==='dashboard'" @click="activeTab='dashboard'" />
      <div role="tabpanel" class="tab-content bg-base-100 border-base-300 rounded-box p-4 sm:p-6 w-full min-w-0" v-show="activeTab==='dashboard'">
        <div class="kpi-lg grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 grid-space-4">
          <KpiCard title="Completed" :value="completedCount" icon="tabler:circle-check" />
          <KpiCard title="Discovered" :value="discoveredCount" icon="tabler:radar-2" />
          <KpiCard title="Scraped" :value="scrapedCount" icon="tabler:world-www" />
          <KpiCard title="Others" :value="skippedPdfCount" icon="tabler:file-type-pdf" />
          <KpiCard title="Failed" :value="failedTotalCount" icon="tabler:alert-circle" />
          <KpiCard title="Overall" :value="progressRate" unit="%" icon="tabler:progress" />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onBeforeUnmount } from 'vue';
import axiosInstance from '@/api/axiosInstance';
import { globalState } from '@/globalState';
import { useI18n } from '@/i18n';
import KpiCard from '@/components/KpiCard.vue';
import { Icon } from '@iconify/vue/dist/offline';
const props = defineProps({
  agentId: { type: String },
  source: { type: Object },
  initialOpen: { type: Boolean, default: false },
  hideToggle: { type: Boolean, default: false }
});
const { t, currentLanguage } = useI18n();
const isRtl = computed(() => ['he', 'ar'].includes(currentLanguage.value) === true);

const filterQuery = ref('');
const statusFilter = ref('');
const uniqueStatuses = computed(() => [...new Set(report.value.map(e => e.status !== undefined && e.status !== null && e.status !== '' ? e.status : 'Unknown'))]);
const filteredReport = computed(() => {
  const q = String(filterQuery.value || '').toLowerCase();
  return report.value.filter(entry => {
    const urlText = entry !== undefined && entry !== null && entry.url !== undefined && entry.url !== null ? String(entry.url).toLowerCase() : '';
    const errorText = entry !== undefined && entry !== null && entry.lastError !== undefined && entry.lastError !== null ? String(entry.lastError).toLowerCase() : '';
    const statusText = entry !== undefined && entry !== null && entry.status !== undefined && entry.status !== null ? String(entry.status).toLowerCase() : '';
    const matchesQuery = q === '' || urlText.includes(q) !== false || errorText.includes(q) !== false || statusText.includes(q) !== false;
    const matchesStatus = statusFilter.value === '' || (statusFilter.value === 'Unknown' ? (entry.status === undefined || entry.status === null || entry.status === '') : entry.status === statusFilter.value);
    return matchesQuery !== false && matchesStatus !== false;
  });
});
const open = ref(props.initialOpen);
const loading = ref(false);
const error = ref('');
const report = ref([]);
const activeTab = ref('table');
const overallStatus = ref('');
const lastErrorMessage = ref('');

const discoveredCount = computed(() => report.value.length);
const completedCount = computed(() => report.value.filter(e => { const s = (e.status || '').toLowerCase(); return s.includes('completed') !== false; }).length);
const failedScrapeCount = computed(() => report.value.filter(e => String(e.status).toLowerCase() === 'failedscrape').length);
const failedVectorizeCount = computed(() => report.value.filter(e => String(e.status).toLowerCase() === 'failedvectorize').length);
const failedDiscoveryCount = computed(() => report.value.filter(e => String(e.status).toLowerCase() === 'faileddiscovery').length);
const skippedPdfCount = computed(() => report.value.filter(e => String(e.status).toLowerCase() === 'skippedpdf').length);
const scrapedCount = computed(() => report.value.filter(e => String(e.status).toLowerCase() === 'scraped').length);
const processingScrapeCount = computed(() => report.value.filter(e => String(e.status).toLowerCase() === 'processingscrape').length);
const processingVectorizeCount = computed(() => report.value.filter(e => String(e.status).toLowerCase() === 'processingvectorize').length);
const processingCount = computed(() => processingScrapeCount.value + processingVectorizeCount.value);
const pendingCount = computed(() => report.value.filter(e => String(e.status).toLowerCase() === 'pending').length);
const failedTotalCount = computed(() => failedScrapeCount.value + failedVectorizeCount.value + failedDiscoveryCount.value);
const processedCount = computed(() => report.value.filter(e => { const s = String(e.status).toLowerCase(); return s !== 'pending' && s !== 'processingscrape' && s !== 'processingvectorize'; }).length);
const progressRate = computed(() => discoveredCount.value > 0 ? Math.round((processedCount.value / discoveredCount.value) * 100) : 0);
const activeStatuses = ['pendingDiscovery', 'discoveringLinks', 'externalPendingDiscovery', 'externalPendingSingleUrl', 'externalProcessing', 'scrapingInProgress'];
const isActiveStatus = computed(() => activeStatuses.includes(overallStatus.value) === true);
const statusTitle = computed(() => {
  const status = overallStatus.value;
  if (status === 'pendingDiscovery' || status === 'discoveringLinks') return 'Discovering links';
  if (status === 'externalPendingDiscovery' || status === 'externalPendingSingleUrl') return 'Waiting for scraper';
  if (status === 'externalProcessing' || status === 'scrapingInProgress') return 'Scraping pages';
  if (status === 'completed') return 'Scraping completed';
  if (status === 'partiallyCompleted') return 'Scraping partially completed';
  if (status === 'failed') return 'Scraping failed';
  return status;
});
const statusMessage = computed(() => {
  const status = overallStatus.value;
  if (status === 'pendingDiscovery' || status === 'discoveringLinks') {
    return `${discoveredCount.value} links found. Scraping starts when link discovery finishes.`;
  }
  if (status === 'externalPendingDiscovery' || status === 'externalPendingSingleUrl') {
    return `${discoveredCount.value} links found. The job is waiting for the scraper worker.`;
  }
  if (status === 'externalProcessing' || status === 'scrapingInProgress') {
    return `${completedCount.value} completed, ${pendingCount.value + processingCount.value + scrapedCount.value} remaining.`;
  }
  if (status === 'completed') {
    return `${completedCount.value} of ${discoveredCount.value} discovered links completed.`;
  }
  if (status === 'partiallyCompleted') {
    return lastErrorMessage.value !== '' ? lastErrorMessage.value : `${completedCount.value} of ${discoveredCount.value} discovered links completed.`;
  }
  if (status === 'failed') {
    return lastErrorMessage.value !== '' ? lastErrorMessage.value : 'The scraping job failed.';
  }
  return `Current job stage: ${status}`;
});
const statusAlertClass = computed(() => {
  if (overallStatus.value === 'completed') return 'alert-success';
  if (overallStatus.value === 'failed') return 'alert-error';
  if (overallStatus.value === 'partiallyCompleted') return 'alert-warning';
  if (isActiveStatus.value === true) return 'alert-info';
  return '';
});

function statusBadgeClass(status) {
  const s = String(status || '').toLowerCase();
  if (s.includes('completed') !== false) return 'inline-block px-2 py-1 rounded-full bg-green-500 text-white';
  if (s === 'scraped') return 'inline-block px-2 py-1 rounded-full bg-orange-500 text-white';
  if (s === 'processingscrape' || s === 'processingvectorize') return 'inline-block px-2 py-1 rounded-full bg-yellow-500 text-white';
  if (s === 'pending') return 'inline-block px-2 py-1 rounded-full bg-gray-500 text-white';
  if (s === 'skippedpdf') return 'inline-block px-2 py-1 rounded-full bg-purple-500 text-white';
  if (s === 'failedscrape' || s === 'failedvectorize' || s === 'faileddiscovery' || s.includes('error') !== false || s.includes('fail') !== false) return 'inline-block px-2 py-1 rounded-full bg-red-500 text-white';
  return 'inline-block px-2 py-1 rounded-full bg-blue-500 text-white';
}

function toggleReport() {
  open.value = open.value === false ? true : false;
  if (open.value !== false && report.value.length === 0) loadReport();
}
async function loadReport() {
  loading.value = true;
  error.value = '';
  try {
    const res = await axiosInstance.get(
      `${globalState.apiEndpoint}/api/agents/${props.agentId}/scrapeReport`,
      { params: { source: props.source.name } }
    );
    const responseData = res.data !== undefined && res.data !== null ? res.data : {};
    overallStatus.value = typeof responseData.overallStatus === 'string' ? responseData.overallStatus : '';
    lastErrorMessage.value = typeof responseData.lastErrorMessage === 'string' ? responseData.lastErrorMessage : '';
    const discovered = responseData.discoveredLinks;
    report.value = Array.isArray(discovered) !== false ? discovered : [];
  } catch (err) {
    error.value = err!= undefined && err.message !== undefined && err.message !== null ? err.message : 'Failed to load report';
  } finally {
    loading.value = false;
  }
}

let intervalId = null;

watch(open, (newVal) => {
  if (newVal === true) {
    loadReport();
    intervalId = setInterval(loadReport, 2000);
  } else {
    if (intervalId !== null) {
      clearInterval(intervalId);
      intervalId = null;
    }
  }
}, { immediate: true });

onBeforeUnmount(() => {
  if (intervalId !== null) {
    clearInterval(intervalId);
    intervalId = null;
  }
});
</script>

<style scoped>
.table-fixed-wrap td{word-break:break-all}
:deep(.kpi-lg) .card{padding:28px}
:deep(.kpi-lg) .card h1{font-size:32px !important}
:deep(.kpi-lg) .card p{font-size:1.1rem}
</style>